import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  VStack,
  HStack,
  Box,
  Card,
  Text,
  Avatar,
  useToast,
  Flex,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { MdSearch } from "react-icons/md";
import axiosInstance from "../routes/axiosInstance";
import { useAuth } from "../context/AuthContext";
import BackButton from "../components/BackButton";

const NewChatPage = () => {
  const { currentUser, checkUserAuth } = useAuth();
  const [searchquery, setSearchquery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const searchUser = async () => {
    if (!searchquery.trim()) {
      toast({
        title: "Search Error",
        description: "Please enter a name or email to search",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setUsers([]);
    setSearchLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };

    try {
      const usersList = await axiosInstance.get(
        `/api/users/search?query=${searchquery}`,
        config,
      );
      setUsers(usersList.data.data);
      setSearchLoading(false);

      if (usersList.data.data.length === 0) {
        toast({
          title: "No Results",
          description: `No users found for "${searchquery}"`,
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      setSearchLoading(false);
      toast({
        title: "Search Error",
        description: error.message || "Failed to search users",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      checkUserAuth(error.status);
    }
  };

  const createChatHandler = async (user) => {
    const createChatPayload = {
      recipientId: user._id,
    };

    setLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };

    try {
      const newGroupData = await axiosInstance.post(
        `/api/chats`,
        createChatPayload,
        config,
      );
      setLoading(false);
      navigate(`/chats/${newGroupData.data.data._id}/messages`);
    } catch (error) {
      setLoading(false);
      toast({
        title: "Chat Creation Error",
        description: error.message || "Failed to create chat",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      checkUserAuth(error.status);
    }
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      searchUser();
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <Flex align="center" spacing={3}>
            <BackButton />
            <Text fontSize="lg" fontWeight="600" color="gray.800" ml={3}>
              New Chat
            </Text>
          </Flex>
        </Card>

        {/* Content */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          <VStack spacing={4} p={4} align="stretch">
            {/* Search Section */}
            <VStack spacing={3} align="stretch">
              <Text fontSize="md" fontWeight="600" color="gray.700">
                Search Users
              </Text>

              <HStack spacing={2}>
                <Input
                  placeholder="Enter name or email..."
                  type="text"
                  value={searchquery}
                  onChange={(e) => setSearchquery(e.target.value)}
                  onKeyDown={handleEnterKeyPress}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="lg"
                  _focus={{
                    borderColor: "teal.400",
                    boxShadow: "0 0 0 1px teal.400",
                    bg: "white",
                  }}
                  _hover={{ borderColor: "gray.400" }}
                />
                <Button
                  colorScheme="teal"
                  onClick={searchUser}
                  isLoading={searchLoading}
                  loadingText="Searching"
                  leftIcon={<Icon as={MdSearch} />}
                  borderRadius="lg"
                  px={6}
                >
                  Search
                </Button>
              </HStack>
            </VStack>

            {/* Search Results */}
            {searchLoading ? (
              <Flex justify="center" py={8}>
                <VStack spacing={3}>
                  <Spinner size="lg" color="teal.500" />
                  <Text fontSize="sm" color="gray.500">
                    Searching users...
                  </Text>
                </VStack>
              </Flex>
            ) : users.length > 0 ? (
              <VStack spacing={3} align="stretch">
                <Text fontSize="md" fontWeight="600" color="gray.700">
                  Search Results ({users.length})
                </Text>

                {users.map((user) => (
                  <Box
                    key={user._id}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    pb={3}
                  >
                    <HStack
                      spacing={3}
                      p={3}
                      bg="gray.50"
                      borderRadius="lg"
                      _hover={{ bg: "gray.100" }}
                    >
                      <Avatar
                        name={user.name}
                        size="md"
                        fontSize="sm"
                        fontWeight="600"
                      />
                      <VStack align="start" spacing={0} flex="1">
                        <Text fontWeight="600" fontSize="md" color="gray.800">
                          {user.name}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {user.email}
                        </Text>
                      </VStack>
                      <Button
                        colorScheme="teal"
                        size="sm"
                        onClick={() => createChatHandler(user)}
                        isLoading={loading}
                        loadingText="Creating..."
                        borderRadius="lg"
                      >
                        Start Chat
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : searchquery && !searchLoading ? (
              <Flex justify="center" py={8}>
                <VStack spacing={3} textAlign="center">
                  <Text fontSize="lg" color="gray.500" fontWeight="500">
                    No users found
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Try searching with a different name or email
                  </Text>
                </VStack>
              </Flex>
            ) : (
              <Flex justify="center" py={8}>
                <VStack spacing={3} textAlign="center">
                  <Text fontSize="lg" color="gray.500" fontWeight="500">
                    Search for users
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Enter a name or email to find users to chat with
                  </Text>
                </VStack>
              </Flex>
            )}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default NewChatPage;
